import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Title } from '~/components';
import { DatePicker, Field } from '~/components/Form';
import { today } from '~/utils/dates';

import type { JobPostForm } from '../types';

import { sectionStyle } from './jobPostSections.css';

type Props = {
  form: UseFormReturn<JobPostForm>;
};

export const JobPostPlanningSection = ({ form }: Props) => {
  const { t } = useTranslation(['jobPosts', 'validation']);

  const { control } = form;

  const minDate = useMemo(() => today(), []);

  return (
    <div className={sectionStyle}>
      <Title as="h2" styleAs="h3">
        {t('jobPosts:createEdit.sections.planning.title')}
      </Title>

      {/* Starts at */}
      <Controller
        control={control}
        name="startsAt"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label optional>{t('jobPosts:fields.startsAt.label')}</Field.Label>

            <DatePicker onBlur={onBlur} onChange={onChange} ref={ref} value={value} />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
      />

      {/* Ends at */}
      <Controller
        control={control}
        name="endsAt"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label optional>{t('jobPosts:fields.endsAt.label')}</Field.Label>

            <DatePicker onBlur={onBlur} onChange={onChange} ref={ref} value={value} />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          validate: {
            dateSameOrAfter: (value, formValues) =>
              !value ||
              !formValues.startsAt ||
              dayjs(value).isSame(dayjs(formValues.startsAt)) ||
              dayjs(value).isAfter(dayjs(formValues.startsAt)) ||
              t('validation:dateSameOrAfter', {
                attribute: t('jobPosts:fields.endsAt.label'),
                after: t('jobPosts:fields.startsAt.label'),
              }),
          },
        }}
      />

      {/* Closes at */}
      <Controller
        control={control}
        name="closesAt"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label optional>{t('jobPosts:fields.closesAt.label')}</Field.Label>

            <DatePicker minDate={minDate} onBlur={onBlur} onChange={onChange} ref={ref} value={value} />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ validate: { dateFuture: (value) => !value || !dayjs(value).isBefore(minDate) || t('validation:dateFuture') } }}
      />
    </div>
  );
};
